import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { showErrorToast } from '../utils/toast';   // Block 233
import { Avatar, Button, Skeleton, StatusPill, TabGroup, Textarea, DashboardCard, useRevealForm } from '../ui';
import './dashboard.css';

// Block B: pastel tokens; the word carries the meaning, the colour only repeats it.
const LEAVE_META = {
  vacation: { label: 'Vacation', emoji: '🏖️', color: 'var(--pastel-blue)' },
  sick:     { label: 'Sick day', emoji: '🤒',  color: 'var(--pastel-coral)' },
  personal: { label: 'Personal day', emoji: '🧘‍♀️', color: 'var(--pastel-lilac)' },
  // hr-five (2026-10-12): the two types Carla added.
  unpaid:   { label: 'Unpaid', emoji: '📅', color: 'var(--pastel-neutral)' },
  compassionate: { label: 'Compassionate', emoji: '🤍', color: 'var(--pastel-mint)' },
};

const STATUS_META = {
  pending:  { tone: 'pending', label: 'Pending' },
  approved: { tone: 'success', label: 'Approved' },
  refused:  { tone: 'warning', label: 'Declined' },
  // Decision 46 (FU-1): pending leave of someone who deleted their account. Nothing to approve.
  withdrawn: { tone: 'neutral', label: 'Withdrawn' },
};

const TABS = [
  { id: 'pending', label: 'Pending', color: 'pastel-orange' },
  { id: 'approved', label: 'Approved', color: 'pastel-mint' },
  { id: 'refused', label: 'Declined', color: 'pastel-pink' },
];

function fmtDate(d) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function LeaveRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isManager, setIsManager] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [refuseId, setRefuseId] = useState(null);
  const refuseRef = useRevealForm(refuseId);   // Decline: the reason box in view, the cursor in it
  const [refuseReason, setRefuseReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const { currentUser } = useCurrentUser();

  // Skip the manager-view probe for users we already know are non-managers
  // (a team member whose teamRole is a plain 'user'/'member' and isn't the
  // owner). Leave management is admin/super_admin only, so this never hides the
  // section from an actual manager — and it avoids a guaranteed 403 in console.
  const definitelyNotManager = !!currentUser?.teamId
    && ['user', 'member'].includes(String(currentUser?.teamRole || '').toLowerCase())
    && !currentUser?.isTeamOwner;

  const load = useCallback(async () => {
    if (definitelyNotManager) { setIsManager(false); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api(`/api/leave/requests?status=${filter}`);
      if (res.ok) {
        const data = await res.json();
        setIsManager(true);
        setRequests(data.requests || []);
      }
    } catch (err) {
      // Block 233: the 403 branch used to sit above, testing res.status after
      // await api(...) — unreachable, because api() throws. So isManager was never
      // set false for a non-manager; the catch silently skipped instead, which
      // happened to look the same only because isManager starts false.
      if (err?.status === 403) setIsManager(false);
      // Any other failure is still skipped quietly: this is a background load on a
      // screen most users cannot see.
    }
    setLoading(false);
  }, [filter, definitelyNotManager]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setProcessing(id);
    try {
      const res = await api(`/api/leave/requests/${id}/approve`, { method: 'PUT' });
      if (res.ok) load();
      else { const d = await res.json(); showErrorToast(d.error || 'Failed to approve'); }
    } catch { showErrorToast('Failed to approve'); }
    setProcessing(null);
  };

  const refuse = async (id) => {
    setProcessing(id);
    try {
      const res = await api(`/api/leave/requests/${id}/refuse`, {
        method: 'PUT',
        body: JSON.stringify({ reason: refuseReason }),
      });
      if (res.ok) { setRefuseId(null); setRefuseReason(''); load(); }
      else { const d = await res.json(); showErrorToast(d.error || 'Failed to decline'); }
    } catch { showErrorToast('Failed to decline'); }
    setProcessing(null);
  };

  // Non-managers see nothing; section is hidden entirely
  if (!loading && !isManager) return null;

  return (
    <DashboardCard tint="pastel-green" title="Leave requests" titleAs="h3">
      <TabGroup label="Leave requests" tabs={TABS} value={filter} onChange={setFilter} idPrefix="dash-leave" />

      {loading ? (
        <Skeleton lines={2} label="Loading leave requests" />
      ) : requests.length === 0 ? (
        <p className="dash-text">
          No {filter === 'refused' ? 'declined' : filter} leave requests.
        </p>
      ) : (
        <div className="dash-leave">
          {requests.map(req => {
            const lm = LEAVE_META[req.type] || LEAVE_META.vacation;
            const sm = STATUS_META[req.status] || STATUS_META.pending;
            const days = Number(req.daysRequested);
            const usedDays = Number(req.teamMember?.usedDays || 0);
            // hr-five: the server says whether this request's type uses the allowance in this
            // business, and time off in lieu is part of the allowance (lib/leaveTypes).
            const total = Math.round(((req.teamMember?.totalAllowance || 28) + Number(req.teamMember?.toilDays || 0)) * 100) / 100;
            const counts = req.countsAgainstAllowance ?? req.type !== 'sick';
            const round2 = (n) => Math.round(n * 100) / 100;
            const remainingAfter = counts ? Math.max(0, round2(total - usedDays - days)) : null;

            return (
              <div key={req.id} className="dash-leave__item">
                <div className="dash-leave__top">
                  <Avatar name={req.user?.name || '?'} size={36} color="pastel-lilac" />

                  <div className="dash-leave__main">
                    <div className="dash-leave__who">
                      <span className="dash-leave__name">{req.user?.name}</span>
                      <span className="ui-pill" style={{ background: lm.color }}>{lm.emoji} {lm.label}</span>
                      <StatusPill tone={sm.tone}>{sm.label}</StatusPill>
                    </div>

                    <span className="dash-text">
                      {fmtDate(req.startDate)} to {fmtDate(req.endDate)}
                      {' · '}
                      <strong>{days} working day{days !== 1 ? 's' : ''}</strong>
                      {remainingAfter !== null && <> · {remainingAfter} remaining after</>}
                    </span>
                    {counts && (
                      <span className="dash-meta">{round2(total - usedDays)} of {total} currently available</span>
                    )}

                    {req.note && <span className="dash-leave__note">"{req.note}"</span>}
                    {req.managerNote && <span className="dash-leave__reason">Reason: {req.managerNote}</span>}
                  </div>
                </div>

                {/* Action area: pending only */}
                {req.status === 'pending' && (
                  <div className="dash-leave__actions">
                    {refuseId === req.id ? (
                      <>
                        <Textarea ref={refuseRef}
                          placeholder="Reason for declining (optional, sent to the employee)"
                          aria-label="Reason for declining"
                          value={refuseReason}
                          onChange={e => setRefuseReason(e.target.value)}
                          rows={2}
                        />
                        <div className="dash-actions">
                          <Button variant="delete" size="sm" onClick={() => refuse(req.id)} disabled={processing === req.id}>
                            {processing === req.id ? 'Declining…' : 'Confirm decline'}
                          </Button>
                          <Button variant="tertiary" size="sm" onClick={() => { setRefuseId(null); setRefuseReason(''); }}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="dash-actions">
                        <Button variant="confirm" size="sm" onClick={() => approve(req.id)} disabled={!!processing}>
                          {processing === req.id ? 'Approving…' : 'Approve'}
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => setRefuseId(req.id)} disabled={!!processing}>
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardCard>
  );
}

export default LeaveRequests;
